import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Save, Send, Trash2, FileDown } from 'lucide-react';
import {
  createRascunho,
  deleteRascunho,
  getRascunho,
  Rascunho,
  RascunhoCTePayload,
  RascunhoNFeItem,
  RascunhoNFePayload,
  TipoDoc,
  TransmissaoResultado,
  transmitirRascunho,
  updateRascunho
} from '../api/documentos';
import { apiError } from '../api/client';
import { getEmitente } from '../api/emitente';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';
import FormField from '../components/FormField';
import LoadingSpinner from '../components/LoadingSpinner';
import Toast, { ToastMsg } from '../components/Toast';
import PessoaAutocomplete from '../components/PessoaAutocomplete';
import ProdutoAutocomplete from '../components/ProdutoAutocomplete';
import CfopBuscaModal from '../components/CfopBuscaModal';
import { Search } from 'lucide-react';
import { createProduto, Produto, proximoCodigoProduto } from '../api/produto';

const NOMES: Record<TipoDoc, string> = {
  nfe: 'NF-e',
  cte: 'CT-e',
  'cte-os': 'CT-e OS',
  mdfe: 'MDF-e'
};

// Motivos retornados por email_autosend.py traduzidos para o operador
const MOTIVOS_EMAIL: Record<string, string> = {
  desativado_no_emitente: 'o envio automático está desativado no cadastro do emitente',
  sem_xml_autorizado: 'o documento ficou sem XML autorizado',
  emitente_nao_encontrado: 'o emitente não foi encontrado',
  homologacao_sem_email_envio_notas:
    'em homologação o e-mail vai só para o "E-mail p/ envio de notas" do emitente, que não está preenchido',
  sem_email_destinatario_e_sem_email_envio_notas:
    'nem o tomador/destinatário nem o emitente têm e-mail cadastrado'
};

interface NFeFormState {
  natureza_operacao: string;
  destinatario_id: string;
  finalidade: string;
  tipo_nf: string;
  ind_final: string;
  ind_pres: string;
  ind_intermed: string;
  intermed_cnpj: string;
  chave_referencia_nfe: string;
  autxml_cpf: string;
  autxml_cnpj: string;
  tipo_pag: string;
  valor_pag: string;
  dh_emi: string;
  dh_sai: string;
  total_frete: string;
  total_seguro: string;
  total_desc: string;
  total_outro: string;
  difal_ativar: boolean;
  difal_vbc: string;
  difal_picmsufdest: string;
  difal_picmsinter: string;
  difal_picmsinterpart: string;
  difal_vicmsufdest: string;
  difal_vicmsufremet: string;
  mod_frete: string;
  transp_doc: string;
  transp_nome: string;
  transp_ie: string;
  transp_endereco: string;
  transp_municipio: string;
  transp_uf: string;
  vol_q: string;
  vol_esp: string;
  vol_marca: string;
  vol_numeracao: string;
  vol_peso_liq: string;
  vol_peso_bruto: string;
  fat_numero: string;
  fat_valor_original: string;
  fat_valor_desconto: string;
  fat_valor_liquido: string;
  dup_numero: string;
  dup_vencimento: string;
  dup_valor: string;
  informacoes_adicionais: string;
  itens: RascunhoNFeItem[];
  numero_proposto: string;
  serie: string;
}

interface QuickProdutoForm {
  open: boolean;
  descricao: string;
  codigo: string;
  ncm: string;
  cfop_padrao: string;
  csosn: string;
  ucom: string;
  valor_unitario: string;
}

interface CTeFormState {
  tomador_id: string;
  remetente_id: string;
  destinatario_id: string;
  expedidor_id: string;
  recebedor_id: string;
  chave_nfe_vinculada: string;
  chave_nfe_original: string;
  chave_cte_pai: string;
  valor_prestacao: string;
  valor_receber: string;
  valor_total_nota: string;
  uf_origem: string;
  uf_destino: string;
  xmun_ini: string;
  xmun_fim: string;
  cmun_ini: string;
  cmun_fim: string;
  cfop: string;
  natureza_operacao: string;
  tp_serv: string;
  tp_cte: string;
  toma: string;
  modal: string;
  produto_predominante: string;
  peso_carga: string;
  tipo_medida: string;
  rntrc: string;
  ciot: string;
  placa: string;
  renavam: string;
  informacoes_adicionais: string;
  percurso_ufs: string;
  desc_servico: string;
  qtd_pessoas: string;
  data_viagem: string;
  hora_viagem: string;
  tipo_fretamento: string;
  taf: string;
  nro_reg_estadual: string;
  numero_proposto: string;
  serie: string;
}

const ITEM_VAZIO: RascunhoNFeItem = {
  produto_id: 0,
  codigo: '',
  descricao: '',
  ncm: '',
  cest: '',
  ucom: 'UN',
  ean: 'SEM GTIN',
  utrib: 'UN',
  qtrib: 1,
  vuntrib: 0,
  orig: '0',
  cfop: '',
  ipi_cst: '53',
  ipi_vbc: 0,
  ipi_pipi: 0,
  ipi_vipi: 0,
  pis_cst: '07',
  pis_vbc: 0,
  pis_ppis: 0,
  pis_vpis: 0,
  cofins_cst: '07',
  cofins_vbc: 0,
  cofins_pcofins: 0,
  cofins_vcofins: 0,
  quantidade: 1,
  valor_unitario: 0,
  desconto: 0,
  inf_ad_prod: ''
};

function emptyState(): NFeFormState {
  return {
    natureza_operacao: 'VENDA',
    destinatario_id: '',
    finalidade: '1',
    tipo_nf: '1',
    ind_final: '1',
    ind_pres: '9',
    ind_intermed: '0',
    intermed_cnpj: '',
    chave_referencia_nfe: '',
    autxml_cpf: '',
    autxml_cnpj: '',
    tipo_pag: '90',
    valor_pag: '',
    dh_emi: '',
    dh_sai: '',
    total_frete: '',
    total_seguro: '',
    total_desc: '',
    total_outro: '',
    difal_ativar: false,
    difal_vbc: '',
    difal_picmsufdest: '',
    difal_picmsinter: '',
    difal_picmsinterpart: '100',
    difal_vicmsufdest: '',
    difal_vicmsufremet: '',
    mod_frete: '9',
    transp_doc: '',
    transp_nome: '',
    transp_ie: '',
    transp_endereco: '',
    transp_municipio: '',
    transp_uf: '',
    vol_q: '',
    vol_esp: '',
    vol_marca: '',
    vol_numeracao: '',
    vol_peso_liq: '',
    vol_peso_bruto: '',
    fat_numero: '',
    fat_valor_original: '',
    fat_valor_desconto: '',
    fat_valor_liquido: '',
    dup_numero: '',
    dup_vencimento: '',
    dup_valor: '',
    informacoes_adicionais: '',
    itens: [{ ...ITEM_VAZIO }],
    numero_proposto: '',
    serie: '1'
  };
}

function formatDecimalBR(value: number, minimumFractionDigits = 2, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits,
    maximumFractionDigits
  }).format(Number.isFinite(value) ? value : 0);
}

function parseDecimalBR(value: string): number {
  const cleaned = value.replace(/\s/g, '');
  const normalized = cleaned.includes(',')
    ? cleaned.replace(/\./g, '').replace(',', '.')
    : cleaned;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseMoedaBR(value: string): number {
  const cleaned = value.replace(/\s/g, '').replace(/^R\$/i, '');
  if (cleaned.includes(',')) return parseDecimalBR(cleaned);
  const degraded = cleaned.match(/^(\d+)\.(\d{3,})(\d{2})$/);
  const normalized = degraded
    ? `${degraded[1]}${degraded[2]}.${degraded[3]}`
    : cleaned;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

interface MDFeCondutor { nome: string; cpf: string; }
interface MDFeDescarga { cmun: string; xmun: string; chaves_nfe: string; chaves_cte: string; }

interface MDFeFormState {
  uf_ini: string;
  uf_fim: string;
  dh_ini_viagem: string;
  cmun_carrega: string;
  xmun_carrega: string;
  percurso_ufs: string;
  rntrc: string;
  placa: string;
  tara: string;
  tp_rod: string;
  tp_car: string;
  uf_veic: string;
  renavam: string;
  ciot: string;
  condutores: MDFeCondutor[];
  descargas: MDFeDescarga[];
  v_carga: string;
  q_carga: string;
  c_unid: string;
  info_adicional: string;
  numero_proposto: string;
  serie: string;
}

function emptyMDFeState(): MDFeFormState {
  return {
    uf_ini: 'PB',
    uf_fim: 'PB',
    dh_ini_viagem: '',
    cmun_carrega: '',
    xmun_carrega: '',
    percurso_ufs: '',
    rntrc: '',
    placa: '',
    tara: '',
    tp_rod: '01',
    tp_car: '02',
    uf_veic: 'PB',
    renavam: '',
    ciot: '',
    condutores: [{ nome: '', cpf: '' }],
    descargas: [{ cmun: '', xmun: '', chaves_nfe: '', chaves_cte: '' }],
    v_carga: '',
    q_carga: '',
    c_unid: '01',
    info_adicional: '',
    numero_proposto: '',
    serie: '1'
  };
}

function emptyCTeState(): CTeFormState {
  return {
    tomador_id: '',
    remetente_id: '',
    destinatario_id: '',
    expedidor_id: '',
    recebedor_id: '',
    chave_nfe_vinculada: '',
    chave_nfe_original: '',
    chave_cte_pai: '',
    valor_prestacao: '',
    valor_receber: '',
    valor_total_nota: '',
    uf_origem: 'PB',
    uf_destino: 'RN',
    xmun_ini: '',
    xmun_fim: '',
    cmun_ini: '',
    cmun_fim: '',
    cfop: '6352',
    natureza_operacao: 'PRESTACAO DE SERVICO DE TRANSPORTE',
    tp_serv: '0',
    tp_cte: '0',
    toma: '0',
    modal: '01',
    produto_predominante: 'MERCADORIAS',
    peso_carga: '1.0',
    tipo_medida: 'PESO BRUTO',
    rntrc: '11111111',
    ciot: '',
    placa: '',
    renavam: '',
    informacoes_adicionais: '',
    percurso_ufs: '',
    desc_servico: 'TRANSPORTE DE PESSOAS',
    qtd_pessoas: '1',
    data_viagem: '',
    hora_viagem: '08:00',
    tipo_fretamento: '1',
    taf: '',
    nro_reg_estadual: '',
    numero_proposto: '',
    serie: '1'
  };
}

export default function RascunhoEditPage() {
  const { tipo, id } = useParams();
  const tipoDoc = (tipo || 'nfe') as TipoDoc;
  const isNew = !id || id === 'novo';
  const rid = isNew ? null : Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { emitenteId } = useEmitenteAtivo();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const duplicando = searchParams.get('duplicar') === 'true' || (location.state as { duplicando?: boolean } | null)?.duplicando === true;

  const [saving, setSaving] = useState(false);
  const [transmitting, setTransmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [result, setResult] = useState<TransmissaoResultado | null>(null);
  const [toast, setToast] = useState<ToastMsg | null>(null);
  const [state, setState] = useState<NFeFormState>(emptyState());
  const [cteState, setCteState] = useState<CTeFormState>(emptyCTeState());
  const [mdfeState, setMdfeState] = useState<MDFeFormState>(emptyMDFeState());
  const [editingItemIdx, setEditingItemIdx] = useState<number | null>(null);
  const [inlineItemInputs, setInlineItemInputs] = useState<Record<string, string>>({});
  const [quickProduto, setQuickProduto] = useState<QuickProdutoForm>({
    open: false,
    descricao: '',
    codigo: '',
    ncm: '',
    cfop_padrao: '',
    csosn: '',
    ucom: 'UN',
    valor_unitario: ''
  });
  const [creatingProduto, setCreatingProduto] = useState(false);
  const [cfopModalOpen, setCfopModalOpen] = useState(false);
  const chaveNfeRef = useRef<HTMLInputElement | null>(null);

  const q = useQuery({
    enabled: !isNew && !!rid,
    queryKey: ['rascunho', tipoDoc, rid],
    queryFn: () => getRascunho(tipoDoc, rid!)
  });
  const activeRid = !isNew && q.data?.id ? Number(q.data.id) : rid;

  useEffect(() => {
    if (!q.data || isNew || !rid) return;
    const loadedId = Number(q.data.id);
    if (loadedId && loadedId !== rid) {
      navigate(`/documentos/${tipoDoc}/${loadedId}`, { replace: true });
    }
  }, [q.data, isNew, rid, tipoDoc, navigate]);

  // Carrega emitente ativo para saber o próximo número proposto e a série padrão
  const emitenteQ = useQuery({
    enabled: !!emitenteId,
    queryKey: ['emitente', emitenteId],
    queryFn: () => getEmitente(emitenteId!),
    staleTime: 30_000,
  });

  // Em rascunho NOVO, pré-preenche numero_proposto + serie a partir do emitente
  useEffect(() => {
    if (!isNew || !emitenteQ.data) return;
    const em = emitenteQ.data;
    if (tipoDoc === 'nfe') {
      const prox = em.prox_numero_nfe;
      const serie = em.serie_nfe;
      setState((s) => ({
        ...s,
        numero_proposto: s.numero_proposto || (prox != null ? String(prox) : ''),
        serie: s.serie && s.serie !== '1' ? s.serie : (serie != null ? String(serie) : s.serie || '1'),
      }));
    } else if (tipoDoc === 'cte' || tipoDoc === 'cte-os') {
      const prox = tipoDoc === 'cte' ? em.prox_numero_cte : em.prox_numero_ctos;
      const serie = tipoDoc === 'cte' ? em.serie_cte : em.serie_ctos;
      setCteState((s) => ({
        ...s,
        numero_proposto: s.numero_proposto || (prox != null ? String(prox) : ''),
        serie: s.serie && s.serie !== '1' ? s.serie : (serie != null ? String(serie) : s.serie || '1'),
      }));
    } else if (tipoDoc === 'mdfe') {
      setMdfeState((s) => ({
        ...s,
        numero_proposto: s.numero_proposto || (em.prox_numero_mdfe != null ? String(em.prox_numero_mdfe) : ''),
        serie: s.serie && s.serie !== '1' ? s.serie : (em.serie_mdfe != null ? String(em.serie_mdfe) : s.serie || '1'),
      }));
    }
  }, [isNew, tipoDoc, emitenteQ.data]);

  useEffect(() => {
    if (!q.data) return;
    const r = q.data as Rascunho;
    if (tipoDoc === 'mdfe') {
      const p = (r.payload || {}) as Record<string, unknown>;
      const cond = Array.isArray(p.condutores) ? p.condutores as Array<{nome?: string; cpf?: string}> : [];
      const desc = Array.isArray(p.descargas) ? p.descargas as Array<{cmun?: string; xmun?: string; chaves_nfe?: string[]; chaves_cte?: string[]}> : [];
      const percurso = Array.isArray(p.percurso_ufs) ? (p.percurso_ufs as string[]).join(',') : '';
      setMdfeState({
        uf_ini: String(p.uf_ini || 'PB'),
        uf_fim: String(p.uf_fim || 'PB'),
        dh_ini_viagem: String(p.dh_ini_viagem || ''),
        cmun_carrega: String(p.cmun_carrega || ''),
        xmun_carrega: String(p.xmun_carrega || ''),
        percurso_ufs: percurso,
        rntrc: String(p.rntrc || ''),
        placa: String(p.placa || ''),
        tara: p.tara != null ? String(p.tara) : '',
        tp_rod: String(p.tp_rod || '01'),
        tp_car: String(p.tp_car || '02'),
        uf_veic: String(p.uf_veic || 'PB'),
        renavam: String(p.renavam || ''),
        ciot: String(p.ciot || ''),
        condutores: cond.length > 0 ? cond.map((c) => ({ nome: String(c.nome || ''), cpf: String(c.cpf || '') })) : [{ nome: '', cpf: '' }],
        descargas: desc.length > 0 ? desc.map((d) => ({
          cmun: String(d.cmun || ''),
          xmun: String(d.xmun || ''),
          chaves_nfe: Array.isArray(d.chaves_nfe) ? d.chaves_nfe.join('\n') : '',
          chaves_cte: Array.isArray(d.chaves_cte) ? d.chaves_cte.join('\n') : ''
        })) : [{ cmun: '', xmun: '', chaves_nfe: '', chaves_cte: '' }],
        v_carga: p.v_carga != null ? String(p.v_carga) : '',
        q_carga: p.q_carga != null ? String(p.q_carga) : '',
        c_unid: String(p.c_unid || '01'),
        info_adicional: String(p.info_adicional || ''),
        numero_proposto: r.numero_proposto ? String(r.numero_proposto) : '',
        serie: r.serie ? String(r.serie) : '1'
      });
      return;
    }
    if (tipoDoc === 'cte' || tipoDoc === 'cte-os') {
      const p = (r.payload || {}) as Partial<RascunhoCTePayload> & Record<string, unknown>;
      const percursoArr = (p as { percurso_ufs?: string[] | string }).percurso_ufs;
      const percursoStr = Array.isArray(percursoArr) ? percursoArr.join(',') : (percursoArr || '');
      setCteState({
        tomador_id: p.tomador_id ? String(p.tomador_id) : '',
        remetente_id: p.remetente_id ? String(p.remetente_id) : '',
        destinatario_id: p.destinatario_id ? String(p.destinatario_id) : '',
        expedidor_id: p.expedidor_id ? String(p.expedidor_id) : '',
        recebedor_id: p.recebedor_id ? String(p.recebedor_id) : '',
        chave_nfe_vinculada: String(p.chave_nfe_vinculada || ''),
        chave_cte_pai: String((p as { chave_cte_pai?: string }).chave_cte_pai || ''),
        chave_nfe_original: String((p as { chave_nfe_original?: string }).chave_nfe_original || ''),
        valor_prestacao: p.valor_prestacao != null ? String(p.valor_prestacao) : '',
        valor_receber: (p as { valor_receber?: number }).valor_receber != null ? String((p as { valor_receber?: number }).valor_receber) : '',
        valor_total_nota: p.valor_total_nota != null ? String(p.valor_total_nota) : '',
        uf_origem: String(p.uf_origem || 'PB'),
        uf_destino: String(p.uf_destino || 'RN'),
        xmun_ini: String((p as { xmun_ini?: string }).xmun_ini || ''),
        xmun_fim: String((p as { xmun_fim?: string }).xmun_fim || ''),
        cmun_ini: String((p as { cmun_ini?: string }).cmun_ini || ''),
        cmun_fim: String((p as { cmun_fim?: string }).cmun_fim || ''),
        cfop: String(p.cfop || '6352'),
        natureza_operacao: String(p.natureza_operacao || 'PRESTACAO DE SERVICO DE TRANSPORTE'),
        tp_serv: String((p as { tp_serv?: string }).tp_serv || '0'),
        tp_cte: String((p as { tp_cte?: string }).tp_cte || '0'),
        toma: String((p as { toma?: string }).toma || '0'),
        modal: String((p as { modal?: string }).modal || '01'),
        produto_predominante: String(p.produto_predominante || 'MERCADORIAS'),
        peso_carga: p.peso_carga != null ? String(p.peso_carga) : '1.0',
        tipo_medida: String((p as { tipo_medida?: string }).tipo_medida || 'PESO BRUTO'),
        rntrc: String(p.rntrc || '11111111'),
        ciot: String((p as { ciot?: string }).ciot || ''),
        placa: String((p as { placa?: string }).placa || ''),
        renavam: String((p as { renavam?: string }).renavam || ''),
        informacoes_adicionais: String((p as { informacoes_adicionais?: string }).informacoes_adicionais || ''),
        percurso_ufs: percursoStr,
        desc_servico: String((p as { desc_servico?: string }).desc_servico || 'TRANSPORTE DE PESSOAS'),
        qtd_pessoas: String((p as { qtd_pessoas?: number | string }).qtd_pessoas || '1'),
        data_viagem: String((p as { data_viagem?: string }).data_viagem || ''),
        hora_viagem: String((p as { hora_viagem?: string }).hora_viagem || '08:00'),
        tipo_fretamento: String((p as { tipo_fretamento?: string | number }).tipo_fretamento || '1'),
        taf: String((p as { taf?: string }).taf || ''),
        nro_reg_estadual: String((p as { nro_reg_estadual?: string }).nro_reg_estadual || ''),
        numero_proposto: r.numero_proposto ? String(r.numero_proposto) : '',
        serie: r.serie ? String(r.serie) : '1'
      });
      return;
    }
    const p = (r.payload || {}) as Partial<RascunhoNFePayload> & Record<string, unknown>;
    const totaisP = p.totais || {};
    const difalP = p.difal || {};
    const transpP = p.transporte || {};
    const transportaP = transpP.transporta || {};
    const volP = (transpP.volumes || [])[0] || {};
    const cobrancaP = p.cobranca || {};
    const fatP = cobrancaP.fatura || {};
    const dupP = (cobrancaP.duplicatas || [])[0] || {};
    setState({
      natureza_operacao: String(p.natureza_operacao || 'VENDA'),
      destinatario_id: p.destinatario_id ? String(p.destinatario_id) : '',
      finalidade: String(p.finalidade || '1'),
      tipo_nf: String(p.tipo_nf ?? '1'),
      ind_final: p.ind_final != null ? String(p.ind_final) : '1',
      ind_pres: p.ind_pres != null ? String(p.ind_pres) : '9',
      ind_intermed: p.ind_intermed != null ? String(p.ind_intermed) : '0',
      intermed_cnpj: String(p.intermed_cnpj || ''),
      chave_referencia_nfe: String(p.chave_referencia_nfe || ''),
      autxml_cpf: String(p.autxml_cpf || ''),
      autxml_cnpj: String(p.autxml_cnpj || ''),
      tipo_pag: String(p.forma_pagamento?.tipo_pag || '90'),
      valor_pag: p.forma_pagamento?.valor_pag != null ? String(p.forma_pagamento.valor_pag) : '',
      dh_emi: String(p.dh_emi || ''),
      dh_sai: String(p.dh_sai || ''),
      total_frete: totaisP.frete != null ? String(totaisP.frete) : '',
      total_seguro: totaisP.seguro != null ? String(totaisP.seguro) : '',
      total_desc: totaisP.desconto != null ? String(totaisP.desconto) : '',
      total_outro: totaisP.outro != null ? String(totaisP.outro) : '',
      difal_ativar: Boolean(difalP.ativar),
      difal_vbc: difalP.vBCUFDest != null ? String(difalP.vBCUFDest) : '',
      difal_picmsufdest: difalP.pICMSUFDest != null ? String(difalP.pICMSUFDest) : '',
      difal_picmsinter: difalP.pICMSInter != null ? String(difalP.pICMSInter) : '',
      difal_picmsinterpart: difalP.pICMSInterPart != null ? String(difalP.pICMSInterPart) : '100',
      difal_vicmsufdest: difalP.vICMSUFDest != null ? String(difalP.vICMSUFDest) : '',
      difal_vicmsufremet: difalP.vICMSUFRemet != null ? String(difalP.vICMSUFRemet) : '',
      mod_frete: transpP.modFrete != null ? String(transpP.modFrete) : '9',
      transp_doc: String(transportaP.documento || ''),
      transp_nome: String(transportaP.nome || ''),
      transp_ie: String(transportaP.ie || ''),
      transp_endereco: String(transportaP.endereco || ''),
      transp_municipio: String(transportaP.municipio || ''),
      transp_uf: String(transportaP.uf || ''),
      vol_q: volP.qVol != null ? String(volP.qVol) : '',
      vol_esp: String(volP.esp || ''),
      vol_marca: String(volP.marca || ''),
      vol_numeracao: String(volP.nVol || ''),
      vol_peso_liq: volP.pesoL != null ? String(volP.pesoL) : '',
      vol_peso_bruto: volP.pesoB != null ? String(volP.pesoB) : '',
      fat_numero: String(fatP.numero || ''),
      fat_valor_original: fatP.valor_original != null ? String(fatP.valor_original) : '',
      fat_valor_desconto: fatP.valor_desconto != null ? String(fatP.valor_desconto) : '',
      fat_valor_liquido: fatP.valor_liquido != null ? String(fatP.valor_liquido) : '',
      dup_numero: String(dupP.numero || ''),
      dup_vencimento: String(dupP.vencimento || ''),
      dup_valor: dupP.valor != null ? String(dupP.valor) : '',
      informacoes_adicionais: String(p.informacoes_adicionais || ''),
      itens: p.itens && p.itens.length > 0 ? p.itens.map((it) => ({ ...ITEM_VAZIO, ...it })) : [{ ...ITEM_VAZIO }],
      numero_proposto: r.numero_proposto ? String(r.numero_proposto) : '',
      serie: r.serie ? String(r.serie) : '1'
    });
  }, [q.data, tipoDoc]);

  // Autofoco no campo chave_nfe_vinculada quando é CT-e duplicado
  useEffect(() => {
    if (tipoDoc === 'cte' && duplicando && chaveNfeRef.current) {
      chaveNfeRef.current.focus();
      chaveNfeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [tipoDoc, duplicando, cteState.chave_nfe_vinculada.length === 0]);

  const totalItens = useMemo(() => {
    return state.itens.reduce((acc, it) => {
      const qty = Number(it.quantidade) || 0;
      const vu = Number(it.valor_unitario) || 0;
      const desc = Number(it.desconto) || 0;
      return acc + Math.max(0, qty * vu - desc);
    }, 0);
  }, [state.itens]);

  function setField<K extends keyof NFeFormState>(k: K, v: NFeFormState[K]) {
    setState((s) => ({ ...s, [k]: v }));
  }

  function updateItem(idx: number, patch: Partial<RascunhoNFeItem>) {
    setState((s) => ({
      ...s,
      itens: s.itens.map((it, i) => (i === idx ? { ...it, ...patch } : it))
    }));
  }

  function itemFromProduto(prod: Produto): RascunhoNFeItem {
    const valor = prod.valor_unitario != null ? Number(prod.valor_unitario) : 0;
    return {
      ...ITEM_VAZIO,
      produto_id: prod.id,
      codigo: prod.codigo || '',
      descricao: prod.descricao || '',
      ncm: prod.ncm || '',
      cest: prod.cest || '',
      ucom: prod.ucom || 'UN',
      utrib: prod.ucom || 'UN',
      quantidade: 1,
      qtrib: 1,
      valor_unitario: valor,
      vuntrib: valor,
      cfop: prod.cfop_padrao || '',
      csosn: prod.csosn || '',
      orig: prod.orig || '0',
      ean: prod.ean || 'SEM GTIN'
    };
  }

  function addProdutoAoItem(prod: Produto | null | undefined) {
    if (!prod) return;
    const novoItem = itemFromProduto(prod);
    setState((s) => {
      const firstEmpty = s.itens.length === 1 && !s.itens[0].produto_id && !s.itens[0].descricao;
      const itens = firstEmpty ? [novoItem] : [...s.itens, novoItem];
      setEditingItemIdx(null);
      return { ...s, itens };
    });
  }

  function abrirCadastroProduto(busca: string) {
    setQuickProduto((s) => ({
      ...s,
      open: true,
      descricao: s.descricao || busca,
      codigo: s.codigo,
      ucom: s.ucom || 'UN'
    }));
    proximoCodigoProduto()
      .then((codigo) => {
        setQuickProduto((s) => s.codigo ? s : { ...s, codigo });
      })
      .catch(() => {});
  }

  async function salvarProdutoRapido() {
    if (!quickProduto.codigo.trim() || !quickProduto.descricao.trim() || !quickProduto.ncm.trim() || !quickProduto.cfop_padrao.trim() || !quickProduto.ucom.trim()) {
      setErr('Informe codigo, descricao, NCM, CFOP e UND para cadastrar o produto.');
      return;
    }
    setCreatingProduto(true);
    setErr(null);
    try {
      const saved = await createProduto({
        codigo: quickProduto.codigo.trim(),
        descricao: quickProduto.descricao.trim(),
        ncm: quickProduto.ncm.trim(),
        cfop_padrao: quickProduto.cfop_padrao.trim(),
        csosn: quickProduto.csosn.trim(),
        ucom: quickProduto.ucom.trim() || 'UN',
        valor_unitario: quickProduto.valor_unitario.trim() ? parseDecimalBR(quickProduto.valor_unitario) : 0,
        tipo: 'produto',
        ativo: true
      });
      qc.invalidateQueries({ queryKey: ['produtos'] });
      addProdutoAoItem(saved);
      setQuickProduto({
        open: false,
        descricao: '',
        codigo: '',
        ncm: '',
        cfop_padrao: '',
        csosn: '',
        ucom: 'UN',
        valor_unitario: ''
      });
    } catch (e) {
      setErr(apiError(e, 'Erro ao cadastrar produto'));
    } finally {
      setCreatingProduto(false);
    }
  }

  function addItem() {
    setState((s) => {
      const nextIdx = s.itens.length;
      setEditingItemIdx(nextIdx);
      return { ...s, itens: [...s.itens, { ...ITEM_VAZIO }] };
    });
  }

  function removeItem(idx: number) {
    setState((s) => ({ ...s, itens: s.itens.filter((_, i) => i !== idx) }));
    setEditingItemIdx((current) => {
      if (current === null) return null;
      if (current === idx) return null;
      return current > idx ? current - 1 : current;
    });
  }

  function buildPayload(): RascunhoNFePayload {
    const numOrUndef = (s: string) => {
      const n = Number(s);
      return s.trim() === '' || isNaN(n) ? undefined : n;
    };
    const strOrUndef = (s: string) => {
      const v = s.trim();
      return v ? v : undefined;
    };
    const totais = {
      frete: numOrUndef(state.total_frete),
      seguro: numOrUndef(state.total_seguro),
      desconto: numOrUndef(state.total_desc),
      outro: numOrUndef(state.total_outro),
    };
    const algumTotal = Object.values(totais).some((v) => v !== undefined);
    const difal = {
      ativar: state.difal_ativar,
      vBCUFDest: numOrUndef(state.difal_vbc),
      pICMSUFDest: numOrUndef(state.difal_picmsufdest),
      pICMSInter: numOrUndef(state.difal_picmsinter),
      pICMSInterPart: numOrUndef(state.difal_picmsinterpart) ?? 100,
      vICMSUFDest: numOrUndef(state.difal_vicmsufdest),
      vICMSUFRemet: numOrUndef(state.difal_vicmsufremet),
    };
    const transporta = {
      // NT 2026.004: documento do transportador (CNPJ/CPF) pode ser alfanumerico.
      // Preservamos letras + uppercase em vez de descartar com .replace(/\D/g, '').
      documento: state.transp_doc.toUpperCase().trim() || undefined,
      nome: strOrUndef(state.transp_nome),
      ie: strOrUndef(state.transp_ie),
      endereco: strOrUndef(state.transp_endereco),
      municipio: strOrUndef(state.transp_municipio),
      uf: strOrUndef(state.transp_uf.toUpperCase()),
    };
    const temTransporta = Object.values(transporta).some(Boolean);
    const volume = {
      qVol: numOrUndef(state.vol_q),
      esp: strOrUndef(state.vol_esp),
      marca: strOrUndef(state.vol_marca),
      nVol: strOrUndef(state.vol_numeracao),
      pesoL: numOrUndef(state.vol_peso_liq),
      pesoB: numOrUndef(state.vol_peso_bruto),
    };
    const temVolume = Object.values(volume).some((v) => v !== undefined);
    const fatura = {
      numero: strOrUndef(state.fat_numero),
      valor_original: numOrUndef(state.fat_valor_original),
      valor_desconto: numOrUndef(state.fat_valor_desconto),
      valor_liquido: numOrUndef(state.fat_valor_liquido),
    };
    const duplicata = {
      numero: strOrUndef(state.dup_numero),
      vencimento: strOrUndef(state.dup_vencimento),
      valor: numOrUndef(state.dup_valor),
    };
    const temFatura = Object.values(fatura).some((v) => v !== undefined);
    const temDuplicata = Object.values(duplicata).some((v) => v !== undefined);
    return {
      natureza_operacao: state.natureza_operacao.trim() || 'VENDA',
      destinatario_id: Number(state.destinatario_id) || 0,
      finalidade: Number(state.finalidade) || 1,
      tipo_nf: Number(state.tipo_nf) || 1,
      ind_final: Number(state.ind_final) || 0,
      ind_pres: Number(state.ind_pres) || 9,
      ind_intermed: Number(state.ind_intermed) || 0,
      // NT 2026.004: CNPJ/CPF podem ser alfanumericos. Preservar letras.
      intermed_cnpj: state.intermed_cnpj.toUpperCase().trim() || undefined,
      chave_referencia_nfe: state.chave_referencia_nfe.toUpperCase().trim() || undefined,
      autxml_cpf: state.autxml_cpf.toUpperCase().trim() || undefined,
      autxml_cnpj: state.autxml_cnpj.toUpperCase().trim() || undefined,
      forma_pagamento: {
        tipo_pag: state.tipo_pag || '90',
        valor_pag: state.tipo_pag === '90' ? 0 : (Number(state.valor_pag) || totalItens)
      },
      dh_emi: state.dh_emi || undefined,
      dh_sai: state.dh_sai || undefined,
      ...(algumTotal ? { totais } : {}),
      ...(state.difal_ativar ? { difal } : {}),
      transporte: {
        modFrete: Number(state.mod_frete) || 9,
        ...(temTransporta ? { transporta } : {}),
        ...(temVolume ? { volumes: [volume] } : {}),
      },
      ...((temFatura || temDuplicata) ? {
        cobranca: {
          ...(temFatura ? { fatura } : {}),
          ...(temDuplicata ? { duplicatas: [duplicata] } : {}),
        }
      } : {}),
      informacoes_adicionais: state.informacoes_adicionais.trim(),
      itens: state.itens
        .filter((it) => Number(it.produto_id) > 0)
        .map((it) => ({
          produto_id: Number(it.produto_id),
          quantidade: Number(it.quantidade) || 0,
          valor_unitario: Number(it.valor_unitario) || 0,
          desconto: Number(it.desconto) || 0,
          cfop: it.cfop || undefined,
          codigo: it.codigo || undefined,
          descricao: it.descricao || undefined,
          ncm: it.ncm || undefined,
          cest: it.cest || undefined,
          ucom: it.ucom || undefined,
          ean: it.ean || undefined,
          utrib: it.utrib || it.ucom || undefined,
          qtrib: Number(it.qtrib) || Number(it.quantidade) || undefined,
          vuntrib: Number(it.vuntrib) || Number(it.valor_unitario) || undefined,
          orig: it.orig || undefined,
          csosn: it.csosn || undefined,
          ipi_cst: it.ipi_cst || undefined,
          ipi_vbc: Number(it.ipi_vbc) || undefined,
          ipi_pipi: Number(it.ipi_pipi) || undefined,
          ipi_vipi: Number(it.ipi_vipi) || undefined,
          pis_cst: it.pis_cst || undefined,
          pis_vbc: Number(it.pis_vbc) || undefined,
          pis_ppis: Number(it.pis_ppis) || undefined,
          pis_vpis: Number(it.pis_vpis) || undefined,
          cofins_cst: it.cofins_cst || undefined,
          cofins_vbc: Number(it.cofins_vbc) || undefined,
          cofins_pcofins: Number(it.cofins_pcofins) || undefined,
          cofins_vcofins: Number(it.cofins_vcofins) || undefined,
          inf_ad_prod: it.inf_ad_prod || undefined,
        }))
    };
  }

  function buildCTePayload(): RascunhoCTePayload {
    const valorPrestacao = parseMoedaBR(cteState.valor_prestacao);
    const valorReceber = cteState.valor_receber.trim() ? parseMoedaBR(cteState.valor_receber) : valorPrestacao;
    const valorTotalNota = cteState.valor_total_nota.trim() ? parseMoedaBR(cteState.valor_total_nota) : valorPrestacao;
    return {
      tomador_id: Number(cteState.tomador_id) || null,
      remetente_id: Number(cteState.remetente_id) || null,
      destinatario_id: Number(cteState.destinatario_id) || null,
      expedidor_id: cteState.expedidor_id ? Number(cteState.expedidor_id) : null,
      recebedor_id: cteState.recebedor_id ? Number(cteState.recebedor_id) : null,
      // NT 2026.004: chaves de acesso podem conter letras (CNPJ alfanumerico).
      // Preservar letras + uppercase.
      chave_nfe_vinculada: cteState.chave_nfe_vinculada.toUpperCase().trim(),
      chave_cte_pai: cteState.tp_serv === '1' ? cteState.chave_cte_pai.toUpperCase().trim() : '',
      valor_prestacao: valorPrestacao,
      valor_receber: valorReceber,
      valor_total_nota: valorTotalNota,
      uf_origem: cteState.uf_origem.trim().toUpperCase() || 'PB',
      uf_destino: cteState.uf_destino.trim().toUpperCase() || 'RN',
      xmun_ini: cteState.xmun_ini.trim(),
      xmun_fim: cteState.xmun_fim.trim(),
      cmun_ini: cteState.cmun_ini.trim(),
      cmun_fim: cteState.cmun_fim.trim(),
      cfop: cteState.cfop.trim() || '6352',
      natureza_operacao: cteState.natureza_operacao.trim() || 'PRESTACAO DE SERVICO DE TRANSPORTE',
      tp_serv: cteState.tp_serv || '0',
      tp_cte: cteState.tp_cte || '0',
      toma: cteState.toma || '0',
      modal: cteState.modal || '01',
      produto_predominante: cteState.produto_predominante.trim() || 'MERCADORIAS',
      peso_carga: Number(cteState.peso_carga) || 1,
      tipo_medida: cteState.tipo_medida || 'PESO BRUTO',
      rntrc: cteState.rntrc.trim() || '11111111',
      ciot: cteState.ciot.trim(),
      placa: cteState.placa.trim(),
      renavam: cteState.renavam.trim(),
      informacoes_adicionais: cteState.informacoes_adicionais.trim(),
      percurso_ufs: cteState.percurso_ufs.split(/[,\s]+/).map((u) => u.trim().toUpperCase()).filter((u) => u.length === 2),
      desc_servico: cteState.desc_servico.trim() || 'TRANSPORTE DE PESSOAS',
      qtd_pessoas: Number(cteState.qtd_pessoas) || 1,
      data_viagem: cteState.data_viagem.trim(),
      hora_viagem: cteState.hora_viagem.trim() || '08:00',
      tipo_fretamento: cteState.tipo_fretamento || '1',
      taf: cteState.taf.trim(),
      nro_reg_estadual: cteState.nro_reg_estadual.trim(),
    } as RascunhoCTePayload;
  }

  function buildMDFePayload(): Record<string, unknown> {
    // Chave de acesso 44 chars pode conter letras (CNPJ alfanumerico - NT 2026.004).
  const toLines = (t: string) => t.split(/[\n,;\s]+/).map((s) => s.toUpperCase().trim()).filter((s) => s.length === 44);
    const percurso = mdfeState.percurso_ufs.split(/[,\s]+/).map((s) => s.trim().toUpperCase()).filter((s) => s.length === 2);
    return {
      uf_ini: mdfeState.uf_ini.trim().toUpperCase() || 'PB',
      uf_fim: mdfeState.uf_fim.trim().toUpperCase() || 'PB',
      dh_ini_viagem: mdfeState.dh_ini_viagem.trim(),
      cmun_carrega: mdfeState.cmun_carrega.replace(/\D/g, ''),
      xmun_carrega: mdfeState.xmun_carrega.trim(),
      percurso_ufs: percurso,
      rntrc: mdfeState.rntrc.replace(/\D/g, ''),
      placa: mdfeState.placa.trim().toUpperCase().replace(/-/g, ''),
      tara: Number(mdfeState.tara) || 0,
      tp_rod: mdfeState.tp_rod || '01',
      tp_car: mdfeState.tp_car || '02',
      uf_veic: mdfeState.uf_veic.trim().toUpperCase(),
      renavam: mdfeState.renavam.replace(/\D/g, ''),
      ciot: mdfeState.ciot.replace(/\D/g, ''),
      condutores: mdfeState.condutores
        .map((c) => ({ nome: c.nome.trim(), cpf: c.cpf.replace(/\D/g, '') }))
        .filter((c) => c.nome && c.cpf.length === 11),
      descargas: mdfeState.descargas.map((d) => ({
        cmun: d.cmun.replace(/\D/g, ''),
        xmun: d.xmun.trim(),
        chaves_nfe: toLines(d.chaves_nfe),
        chaves_cte: toLines(d.chaves_cte)
      })).filter((d) => d.cmun && (d.chaves_nfe.length > 0 || d.chaves_cte.length > 0)),
      v_carga: Number(mdfeState.v_carga) || 0,
      q_carga: Number(mdfeState.q_carga) || 0,
      c_unid: mdfeState.c_unid || '01',
      info_adicional: mdfeState.info_adicional.trim()
    };
  }

  /**
   * Salva o rascunho. Se `transmitirEmSeguida=true` e o save der certo,
   * já chama a transmissão SEFAZ sem exigir um segundo clique.
   */
  async function onSave(e: React.FormEvent | null, transmitirEmSeguida = false) {
    if (e) e.preventDefault();
    if (transmitirEmSeguida && tipoDoc === 'nfe' && !Number(state.destinatario_id)) {
      setErr('Selecione o destinatário antes de emitir a NF-e.');
      return;
    }
    setSaving(true);
    setErr(null);
    setInfo(null);
    setResult(null);
    try {
      const isCTe = tipoDoc === 'cte' || tipoDoc === 'cte-os';
      const isMDFeSave = tipoDoc === 'mdfe';
      const payload = isCTe ? buildCTePayload() : (isMDFeSave ? buildMDFePayload() : buildPayload());
      const numeroProp = isCTe
        ? (cteState.numero_proposto ? Number(cteState.numero_proposto) : null)
        : (isMDFeSave
          ? (mdfeState.numero_proposto ? Number(mdfeState.numero_proposto) : null)
          : (state.numero_proposto ? Number(state.numero_proposto) : null));
      const serieVal = isCTe
        ? (cteState.serie ? Number(cteState.serie) : 1)
        : (isMDFeSave
          ? (mdfeState.serie ? Number(mdfeState.serie) : 1)
          : (state.serie ? Number(state.serie) : 1));
      const base: Partial<Rascunho> = {
        emitente_id: emitenteId ?? undefined,
        numero_proposto: numeroProp,
        serie: serieVal,
        payload
      } as Partial<Rascunho>;

      let saved: Rascunho;
      if (isNew) saved = await createRascunho(tipoDoc, base);
      else saved = await updateRascunho(tipoDoc, activeRid!, base);
      qc.invalidateQueries({ queryKey: ['rascunhos', tipoDoc] });

      if (transmitirEmSeguida) {
        // encadeia a transmissão logo depois de salvar, usando o id resultante
        setSaving(false);
        await transmitirPorId(saved.id);
        if (isNew) navigate(`/documentos/${tipoDoc}/${saved.id}`, { replace: true });
        return;
      }

      if (isNew) navigate(`/documentos/${tipoDoc}/${saved.id}`, { replace: true });
      setInfo('Rascunho salvo.');
    } catch (e) {
      setErr(apiError(e, 'Erro ao salvar rascunho'));
    } finally {
      setSaving(false);
    }
  }

  async function transmitirPorId(id: number) {
    setTransmitting(true);
    setErr(null);
    setInfo(null);
    setResult(null);
    setToast({
      tipo: 'info',
      msg: `Transmitindo ${NOMES[tipoDoc]} à SEFAZ e enviando e-mail automático...`,
      duracao: 0
    });
    try {
      const r = await transmitirRascunho(tipoDoc, id);
      setResult(r);
      qc.invalidateQueries({ queryKey: ['rascunho', tipoDoc, id] });
      qc.invalidateQueries({ queryKey: ['rascunhos'] });
      qc.invalidateQueries({ queryKey: ['documentos-emitidos'] });
      qc.invalidateQueries({ queryKey: ['contadores-documentos'] });
      if (r.sucesso) {
        setInfo(`${NOMES[tipoDoc]} autorizado. cStat=${r.cStat} — ${r.xMotivo}`);
        const em = r.email_automatico;
        if (em?.enviado) {
          setToast({
            tipo: 'ok',
            msg: `E-mail enviado para ${(em.para ?? []).join(', ')} (XML + PDF em anexo).`
          });
        } else if (em) {
          setToast({
            tipo: 'err',
            msg: `${NOMES[tipoDoc]} autorizado, mas o e-mail automático não foi enviado: ${
              MOTIVOS_EMAIL[em.motivo ?? ''] ?? em.motivo ?? 'motivo desconhecido'
            }.`
          });
        } else {
          setToast(null);
        }
      } else {
        setToast(null);
        setErr(`Transmissao falhou (cStat=${r.cStat || '-'}): ${r.xMotivo || r.erro || 'sem detalhe'}`);
      }
    } catch (e) {
      setToast(null);
      setErr(apiError(e, 'Erro na transmissao'));
    } finally {
      setTransmitting(false);
    }
  }

  async function onTransmitir() {
    if (!activeRid) return;
    if (tipoDoc === 'nfe' && !Number(state.destinatario_id)) {
      setErr('Selecione o destinatário antes de emitir a NF-e.');
      return;
    }
    await transmitirPorId(activeRid);
  }

  async function onDelete() {
    if (!activeRid) return;
    if (!confirm('Excluir este rascunho?')) return;
    try {
      await deleteRascunho(tipoDoc, activeRid);
      qc.invalidateQueries({ queryKey: ['rascunhos', tipoDoc] });
      navigate('/documentos', { replace: true });
    } catch (e) {
      setErr(apiError(e, 'Erro ao excluir'));
    }
  }

  if (!isNew && q.isLoading) {
    return <div className="py-10 grid place-items-center"><LoadingSpinner /></div>;
  }

  const isNFe = tipoDoc === 'nfe';
  const isCTe = tipoDoc === 'cte' || tipoDoc === 'cte-os';
  const isMDFe = tipoDoc === 'mdfe';
  const numeroDoc = isNFe ? state.numero_proposto : isCTe ? cteState.numero_proposto : mdfeState.numero_proposto;
  const tituloRascunho = isNew
    ? `Novo rascunho ${NOMES[tipoDoc]}`
    : `Rascunho ${NOMES[tipoDoc]} #${activeRid}${numeroDoc ? `, número ${numeroDoc}` : ''}`;

  const totalResumo = isNFe
    ? totalItens
    : isCTe
      ? parseMoedaBR(cteState.valor_prestacao)
      : isMDFe
        ? Number(mdfeState.v_carga) || 0
        : 0;
  const serieResumo = isNFe ? state.serie : isCTe ? cteState.serie : mdfeState.serie;
  const statusResumo = isNew ? 'Novo' : 'Editando';

  function setCteField<K extends keyof CTeFormState>(k: K, v: CTeFormState[K]) {
    setCteState((s) => ({ ...s, [k]: v }));
  }

  function setValorPrestacaoCTe(raw: string) {
    setCteState((s) => {
      const valorReceberSegueFrete = !s.valor_receber.trim() || parseMoedaBR(s.valor_receber) === parseMoedaBR(s.valor_prestacao);
      const valorCargaSegueFrete = !s.valor_total_nota.trim() || parseMoedaBR(s.valor_total_nota) === parseMoedaBR(s.valor_prestacao);
      return {
        ...s,
        valor_prestacao: raw,
        valor_receber: valorReceberSegueFrete ? raw : s.valor_receber,
        valor_total_nota: valorCargaSegueFrete ? raw : s.valor_total_nota,
      };
    });
  }

  function formatarValorPrestacaoCTe(raw: string) {
    const formatted = formatDecimalBR(parseMoedaBR(raw));
    setCteState((s) => {
      const valorReceberSegueFrete = !s.valor_receber.trim() || parseMoedaBR(s.valor_receber) === parseMoedaBR(s.valor_prestacao);
      const valorCargaSegueFrete = !s.valor_total_nota.trim() || parseMoedaBR(s.valor_total_nota) === parseMoedaBR(s.valor_prestacao);
      return {
        ...s,
        valor_prestacao: formatted,
        valor_receber: valorReceberSegueFrete ? formatted : s.valor_receber,
        valor_total_nota: valorCargaSegueFrete ? formatted : s.valor_total_nota,
      };
    });
  }

  function setMdfeField<K extends keyof MDFeFormState>(k: K, v: MDFeFormState[K]) {
    setMdfeState((s) => ({ ...s, [k]: v }));
  }
  function updateCondutor(idx: number, patch: Partial<MDFeCondutor>) {
    setMdfeState((s) => ({ ...s, condutores: s.condutores.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  }
  function addCondutor() { setMdfeState((s) => ({ ...s, condutores: [...s.condutores, { nome: '', cpf: '' }] })); }
  function removeCondutor(idx: number) { setMdfeState((s) => ({ ...s, condutores: s.condutores.filter((_, i) => i !== idx) })); }
  function updateDescarga(idx: number, patch: Partial<MDFeDescarga>) {
    setMdfeState((s) => ({ ...s, descargas: s.descargas.map((d, i) => (i === idx ? { ...d, ...patch } : d)) }));
  }
  function addDescarga() { setMdfeState((s) => ({ ...s, descargas: [...s.descargas, { cmun: '', xmun: '', chaves_nfe: '', chaves_cte: '' }] })); }
  function removeDescarga(idx: number) { setMdfeState((s) => ({ ...s, descargas: s.descargas.filter((_, i) => i !== idx) })); }

  return (
    <div className="space-y-5">
      <header className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none md:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Link to="/documentos" className="btn-ghost !h-9 !w-9 !px-2 !py-1" title="Voltar para documentos">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <div className="eyebrow">{NOMES[tipoDoc]} / rascunho</div>
                <h1 className="h-display mt-1 text-2xl text-slate-950 md:text-3xl dark:text-slate-100">
                  {tituloRascunho}
                </h1>
              </div>
            </div>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-400">
              Revise os campos fiscais antes de transmitir. A emissão usa o ambiente configurado no emitente ativo.
            </p>
          </div>

          <div className="grid grid-cols-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 text-sm dark:border-slate-800 dark:bg-slate-950/80 lg:min-w-[360px]">
            <div className="border-r border-slate-200 p-3 dark:border-slate-800">
              <div className="mono text-slate-950 dark:text-slate-100">{statusResumo}</div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Status</div>
            </div>
            <div className="border-r border-slate-200 p-3 dark:border-slate-800">
              <div className="mono text-slate-950 dark:text-slate-100">{serieResumo || '1'}</div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">Série</div>
            </div>
            <div className="p-3">
              <div className="mono text-emerald-700 dark:text-emerald-300">R$ {formatDecimalBR(totalResumo)}</div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">Total</div>
            </div>
          </div>
        </div>
      </header>

      <form onSubmit={onSave} className="space-y-4">
        {isNFe && (
          <section className="card-surface p-4 space-y-4">
            <h2 className="font-semibold text-sm uppercase tracking-wide text-neutral-600 dark:text-neutral-400">Dados NF-e</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="field-label">Natureza da operação</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="field-input flex-1"
                    value={state.natureza_operacao}
                    onChange={(e) => setField('natureza_operacao', e.target.value)}
                    placeholder='Ex: Venda de produção do estabelecimento'
                  />
                  <button
                    type="button"
                    className="btn-secondary !px-3 shrink-0"
                    onClick={() => setCfopModalOpen(true)}
                    title="Buscar CFOP / natureza de operação"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div>
                <label className="field-label">Finalidade</label>
                <select
                  className="field-input"
                  value={state.finalidade}
                  onChange={(e) => setField('finalidade', e.target.value)}
                >
                  <option value="1">1 - Normal</option>
                  <option value="2">2 - Complementar</option>
                  <option value="3">3 - Ajuste</option>
                  <option value="4">4 - Devolução</option>
                </select>
              </div>

              <PessoaAutocomplete
                label="Destinatário"
                value={state.destinatario_id}
                onChange={(id) => setField('destinatario_id', id ? String(id) : '')}
                required
              />
              <FormField
                label="Número proposto (opc.)"
                type="number"
                value={state.numero_proposto}
                onChange={(e) => setField('numero_proposto', e.target.value)}
              />
              <FormField
                label="Serie"
                type="number"
                value={state.serie}
                onChange={(e) => setField('serie', e.target.value)}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm">Itens</h3>
                <button type="button" onClick={addItem} className="btn-secondary !py-1">
                  <Plus className="h-4 w-4" /> Item avulso
                </button>
              </div>
              <div className="mb-3 rounded-md border border-neutral-200 bg-neutral-50/70 p-3 dark:border-neutral-700 dark:bg-slate-900/40">
                <ProdutoAutocomplete
                  label="Adicionar produto do estoque"
                  value={null}
                  onChange={(_, prod) => addProdutoAoItem(prod)}
                  onCreateNew={abrirCadastroProduto}
                  placeholder="Buscar por codigo, descricao ou EAN (min. 3 caracteres)..."
                />
                {quickProduto.open && (
                  <div className="mt-3 border-t border-neutral-200 pt-3 dark:border-neutral-800">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <FormField
                        label="Código"
                        className="col-span-12 md:col-span-2"
                        value={quickProduto.codigo}
                        onChange={(e) => setQuickProduto((s) => ({ ...s, codigo: e.target.value }))}
                      />
                      <FormField
                        label="Descrição"
                        className="col-span-12 md:col-span-4"
                        value={quickProduto.descricao}
                        onChange={(e) => setQuickProduto((s) => ({ ...s, descricao: e.target.value }))}
                      />
                      <FormField
                        label="NCM"
                        className="col-span-6 md:col-span-2"
                        value={quickProduto.ncm}
                        onChange={(e) => setQuickProduto((s) => ({ ...s, ncm: e.target.value }))}
                      />
                      <FormField
                        label="CFOP"
                        className="col-span-6 md:col-span-1"
                        value={quickProduto.cfop_padrao}
                        onChange={(e) => setQuickProduto((s) => ({ ...s, cfop_padrao: e.target.value }))}
                      />
                      <FormField
                        label="CSOSN"
                        className="col-span-6 md:col-span-1"
                        value={quickProduto.csosn}
                        onChange={(e) => setQuickProduto((s) => ({ ...s, csosn: e.target.value }))}
                      />
                      <FormField
                        label="UND"
                        className="col-span-6 md:col-span-1"
                        value={quickProduto.ucom}
                        onChange={(e) => setQuickProduto((s) => ({ ...s, ucom: e.target.value }))}
                      />
                      <FormField
                        label="Preço"
                        className="col-span-6 md:col-span-1"
                        value={quickProduto.valor_unitario}
                        onChange={(e) => setQuickProduto((s) => ({ ...s, valor_unitario: e.target.value }))}
                        onFocus={(e) => e.currentTarget.select()}
                      />
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        type="button"
                        className="btn-secondary !py-1.5"
                        onClick={() => setQuickProduto((s) => ({ ...s, open: false }))}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn-primary !py-1.5"
                        disabled={creatingProduto}
                        onClick={salvarProdutoRapido}
                      >
                        <Plus className="h-4 w-4" />
                        {creatingProduto ? 'Salvando...' : 'Cadastrar e inserir'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-700">
                <div className="min-w-[1120px]">
                  <div className="grid grid-cols-[48px_110px_minmax(260px,1fr)_100px_78px_64px_96px_110px_110px_96px] gap-2 bg-neutral-100/70 dark:bg-slate-900/70 px-3 py-2 text-[11px] font-semibold uppercase text-neutral-500">
                    <div>Item</div>
                    <div>Código</div>
                    <div>Descrição</div>
                    <div>CST/CSOSN</div>
                    <div>CFOP</div>
                    <div>UND</div>
                    <div>QTD</div>
                    <div>Preço</div>
                    <div>Total</div>
                    <div className="text-right">Ações</div>
                  </div>
                  <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
                    {state.itens.map((it, idx) => {
                      const quantidade = Number(it.quantidade) || 0;
                      const preco = Number(it.valor_unitario) || 0;
                      const desconto = Number(it.desconto) || 0;
                      const total = Math.max(0, quantidade * preco - desconto);
                      const expanded = editingItemIdx === idx;
                      const qtdKey = `${idx}:qtd`;
                      const precoKey = `${idx}:preco`;
                      return (
                        <div key={idx} className="bg-white dark:bg-slate-950/40">
                          <div className="grid grid-cols-[48px_110px_minmax(260px,1fr)_100px_78px_64px_96px_110px_110px_96px] gap-2 items-center px-3 py-2 text-sm">
                            <div className="font-mono text-xs text-neutral-500">{idx + 1}</div>
                            <div className="truncate font-mono text-xs text-neutral-600 dark:text-neutral-300" title={it.codigo || ''}>
                              {it.codigo || '-'}
                            </div>
                            <div className="truncate font-medium text-neutral-900 dark:text-neutral-100" title={it.descricao || ''}>
                              {it.descricao || 'Produto sem descrição'}
                            </div>
                            <div className="font-mono text-xs text-neutral-700 dark:text-neutral-300">{it.csosn || '-'}</div>
                            <div className="font-mono text-xs text-neutral-700 dark:text-neutral-300">{it.cfop || '-'}</div>
                            <div className="font-mono text-xs text-neutral-700 dark:text-neutral-300">{it.ucom || 'UN'}</div>
                            <input
                              className="field-input !h-9 !py-1 !px-2 text-right"
                              type="text"
                              inputMode="decimal"
                              value={inlineItemInputs[qtdKey] ?? formatDecimalBR(quantidade, 0, 4)}
                              onFocus={(e) => {
                                setInlineItemInputs((s) => ({ ...s, [qtdKey]: formatDecimalBR(quantidade, 0, 4) }));
                                e.currentTarget.select();
                              }}
                              onChange={(e) => {
                                const text = e.target.value;
                                const value = parseDecimalBR(text);
                                setInlineItemInputs((s) => ({ ...s, [qtdKey]: text }));
                                updateItem(idx, { quantidade: value, qtrib: value });
                              }}
                              onBlur={() => {
                                setInlineItemInputs((s) => {
                                  const next = { ...s };
                                  delete next[qtdKey];
                                  return next;
                                });
                              }}
                            />
                            <input
                              className="field-input !h-9 !py-1 !px-2 text-right"
                              type="text"
                              inputMode="decimal"
                              value={inlineItemInputs[precoKey] ?? formatDecimalBR(preco, 2, 2)}
                              onFocus={(e) => {
                                setInlineItemInputs((s) => ({ ...s, [precoKey]: formatDecimalBR(preco, 2, 2) }));
                                e.currentTarget.select();
                              }}
                              onChange={(e) => {
                                const text = e.target.value;
                                const value = parseDecimalBR(text);
                                setInlineItemInputs((s) => ({ ...s, [precoKey]: text }));
                                updateItem(idx, { valor_unitario: value, vuntrib: value });
                              }}
                              onBlur={() => {
                                setInlineItemInputs((s) => {
                                  const next = { ...s };
                                  delete next[precoKey];
                                  return next;
                                });
                              }}
                            />
                            <div className="text-right font-semibold text-neutral-900 dark:text-neutral-100">
                              R$ {formatDecimalBR(total)}
                            </div>
                            <div className="flex justify-end gap-1">
                              <button
                                type="button"
                                className="btn-secondary !h-8 !py-1 !px-2 text-xs"
                                onClick={() => setEditingItemIdx(expanded ? null : idx)}
                                title="Editar campos fiscais do item"
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                className="btn-ghost !h-8 !py-1 !px-2 text-rose-700 hover:text-rose-800 dark:text-rose-400 dark:hover:text-rose-300"
                                onClick={() => removeItem(idx)}
                                title="Excluir item"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          {expanded && (
                            <div className="border-t border-neutral-200 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-slate-900/50">
                              <div className="grid grid-cols-12 gap-2 items-end">
                                <div className="col-span-12 md:col-span-6">
                                  <ProdutoAutocomplete
                                    label="Produto"
                                    value={it.produto_id || null}
                                    onChange={(pid, prod) => updateItem(idx, {
                                      produto_id: pid || 0,
                                      codigo: prod?.codigo || '',
                                      descricao: prod?.descricao || '',
                                      ncm: prod?.ncm || '',
                                      ucom: prod?.ucom || it.ucom || 'UN',
                                      utrib: prod?.ucom || it.utrib || it.ucom || 'UN',
                                      qtrib: it.quantidade || 1,
                                      vuntrib: prod?.valor_unitario != null ? Number(prod.valor_unitario) : (it.vuntrib || it.valor_unitario),
                                      cfop: (prod?.cfop_padrao || it.cfop || '') as string,
                                      csosn: prod?.csosn || it.csosn || '',
                                      valor_unitario: prod?.valor_unitario != null ? Number(prod.valor_unitario) : it.valor_unitario,
                                    })}
                                  />
                                </div>
                                <FormField label="Descrição (xProd)" className="col-span-12 md:col-span-6" value={it.descricao || ''} onChange={(e) => updateItem(idx, { descricao: e.target.value })} />
                                <FormField label="Código" className="col-span-4 md:col-span-2" value={it.codigo || ''} onChange={(e) => updateItem(idx, { codigo: e.target.value })} />
                                <FormField label="NCM" className="col-span-4 md:col-span-2" value={it.ncm || ''} onChange={(e) => updateItem(idx, { ncm: e.target.value })} />
                                <FormField label="CEST" className="col-span-4 md:col-span-2" value={it.cest || ''} onChange={(e) => updateItem(idx, { cest: e.target.value })} />
                                <FormField label="CFOP" className="col-span-4 md:col-span-2" value={it.cfop || ''} onChange={(e) => updateItem(idx, { cfop: e.target.value })} />
                                <FormField label="CSOSN" className="col-span-4 md:col-span-2" value={it.csosn || ''} onChange={(e) => updateItem(idx, { csosn: e.target.value })} />
                                <FormField label="Origem" className="col-span-4 md:col-span-2" value={it.orig || ''} onChange={(e) => updateItem(idx, { orig: e.target.value })} maxLength={1} />
                                <FormField label="UND" className="col-span-4 md:col-span-2" value={it.ucom || ''} onChange={(e) => updateItem(idx, { ucom: e.target.value })} />
                                <FormField label="EAN" className="col-span-8 md:col-span-3" value={it.ean || ''} onChange={(e) => updateItem(idx, { ean: e.target.value })} />
                                <FormField label="uTrib" className="col-span-4 md:col-span-1" value={it.utrib || ''} onChange={(e) => updateItem(idx, { utrib: e.target.value })} />
                                <FormField label="qTrib" type="number" step="0.0001" className="col-span-4 md:col-span-2" value={it.qtrib ?? it.quantidade} onChange={(e) => updateItem(idx, { qtrib: Number(e.target.value) })} />
                                <FormField label="vUnTrib" type="number" step="0.0001" className="col-span-4 md:col-span-2" value={it.vuntrib ?? it.valor_unitario} onChange={(e) => updateItem(idx, { vuntrib: Number(e.target.value) })} />
                                <FormField label="Desc" type="number" step="0.01" className="col-span-4 md:col-span-2" value={it.desconto || 0} onChange={(e) => updateItem(idx, { desconto: Number(e.target.value) })} />
                              </div>
                              <div className="mt-3 grid grid-cols-12 gap-2 items-end">
                                <FormField label="IPI CST" className="col-span-3 md:col-span-1" value={it.ipi_cst || '53'} onChange={(e) => updateItem(idx, { ipi_cst: e.target.value })} />
                                <FormField label="IPI BC" type="number" step="0.01" className="col-span-3 md:col-span-1" value={it.ipi_vbc || 0} onChange={(e) => updateItem(idx, { ipi_vbc: Number(e.target.value) })} />
                                <FormField label="IPI %" type="number" step="0.01" className="col-span-3 md:col-span-1" value={it.ipi_pipi || 0} onChange={(e) => updateItem(idx, { ipi_pipi: Number(e.target.value) })} />
                                <FormField label="IPI valor" type="number" step="0.01" className="col-span-3 md:col-span-1" value={it.ipi_vipi || 0} onChange={(e) => updateItem(idx, { ipi_vipi: Number(e.target.value) })} />
                                <FormField label="PIS CST" className="col-span-3 md:col-span-1" value={it.pis_cst || '07'} onChange={(e) => updateItem(idx, { pis_cst: e.target.value })} />
                                <FormField label="PIS BC" type="number" step="0.01" className="col-span-3 md:col-span-1" value={it.pis_vbc || 0} onChange={(e) => updateItem(idx, { pis_vbc: Number(e.target.value) })} />
                                <FormField label="PIS %" type="number" step="0.01" className="col-span-3 md:col-span-1" value={it.pis_ppis || 0} onChange={(e) => updateItem(idx, { pis_ppis: Number(e.target.value) })} />
                                <FormField label="PIS valor" type="number" step="0.01" className="col-span-3 md:col-span-1" value={it.pis_vpis || 0} onChange={(e) => updateItem(idx, { pis_vpis: Number(e.target.value) })} />
                                <FormField label="COFINS CST" className="col-span-3 md:col-span-1" value={it.cofins_cst || '07'} onChange={(e) => updateItem(idx, { cofins_cst: e.target.value })} />
                                <FormField label="COFINS BC" type="number" step="0.01" className="col-span-3 md:col-span-1" value={it.cofins_vbc || 0} onChange={(e) => updateItem(idx, { cofins_vbc: Number(e.target.value) })} />
                                <FormField label="COFINS %" type="number" step="0.01" className="col-span-3 md:col-span-1" value={it.cofins_pcofins || 0} onChange={(e) => updateItem(idx, { cofins_pcofins: Number(e.target.value) })} />
                                <FormField label="COFINS valor" type="number" step="0.01" className="col-span-3 md:col-span-1" value={it.cofins_vcofins || 0} onChange={(e) => updateItem(idx, { cofins_vcofins: Number(e.target.value) })} />
                              </div>
                              <div className="mt-3">
                                <label className="field-label">Observacao do item (infAdProd)</label>
                                <textarea
                                  className="field-input"
                                  rows={2}
                                  maxLength={500}
                                  placeholder="Informacoes adicionais especificas deste item (max. 500 caracteres)"
                                  value={it.inf_ad_prod || ''}
                                  onChange={(e) => updateItem(idx, { inf_ad_prod: e.target.value })}
                                />
                                <p className="mt-1 text-xs text-neutral-500">{(it.inf_ad_prod || '').length}/500</p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                NCM, CSOSN, origem e demais tributos vem da tabela produto. Valor calculado: <strong>R$ {formatDecimalBR(totalItens)}</strong>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="field-label">Forma pagamento</label>
                <select
                  className="field-input"
                  value={state.tipo_pag}
                  onChange={(e) => setField('tipo_pag', e.target.value)}
                >
                  <option value="01">01 - Dinheiro</option>
                  <option value="03">03 - Cartao credito</option>
                  <option value="04">04 - Cartao debito</option>
                  <option value="15">15 - Boleto</option>
                  <option value="17">17 - PIX</option>
                  <option value="90">90 - Sem pagamento</option>
                </select>
              </div>
              <FormField
                label="Valor pagamento (vazio = total itens)"
                type="number"
                step="0.01"
                value={state.valor_pag}
                onChange={(e) => setField('valor_pag', e.target.value)}
              />
              <div>
                <label className="field-label">Tipo NF</label>
                <select
                  className="field-input"
                  value={state.tipo_nf}
                  onChange={(e) => setField('tipo_nf', e.target.value)}
                >
                  <option value="1">1 - Saida</option>
                  <option value="0">0 - Entrada</option>
                </select>
              </div>
              <div>
                <label className="field-label">Consumidor final (indFinal)</label>
                <select
                  className="field-input"
                  value={state.ind_final}
                  onChange={(e) => setField('ind_final', e.target.value)}
                >
                  <option value="1">1 - Sim</option>
                  <option value="0">0 - Não</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="field-label">Presença comprador (indPres)</label>
                <select
                  className="field-input"
                  value={state.ind_pres}
                  onChange={(e) => setField('ind_pres', e.target.value)}
                >
                  <option value="0">0 - Não se aplica</option>
                  <option value="1">1 - Operacao presencial</option>
                  <option value="2">2 - Internet</option>
                  <option value="3">3 - Teleatendimento</option>
                  <option value="4">4 - NFC-e em domicílio</option>
                  <option value="5">5 - Presencial fora estabelecimento</option>
                  <option value="9">9 - Outros</option>
                </select>
              </div>
              <div>
                <label className="field-label">Intermediador</label>
                <select
                  className="field-input"
                  value={state.ind_intermed}
                  onChange={(e) => setField('ind_intermed', e.target.value)}
                >
                  <option value="0">0 - Sem intermediador</option>
                  <option value="1">1 - Com intermediador</option>
                </select>
              </div>
              {state.ind_intermed === '1' && (
                <FormField
                  label="CNPJ intermediador"
                  value={state.intermed_cnpj}
                  onChange={(e) => setField('intermed_cnpj', e.target.value)}
                  maxLength={18}
                />
              )}
              <FormField
                label="Data emissão (vazio = agora)"
                type="datetime-local"
                value={state.dh_emi}
                onChange={(e) => setField('dh_emi', e.target.value)}
              />
              <FormField
                label="Data saída/entrada (opc.)"
                type="datetime-local"
                value={state.dh_sai}
                onChange={(e) => setField('dh_sai', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                label="Chave NF-e referenciada"
                value={state.chave_referencia_nfe}
                onChange={(e) => setField('chave_referencia_nfe', e.target.value)}
                maxLength={44}
                hint="Obrigatória para devolução (finNFe=4)."
              />
              <FormField
                label="AutXML CPF"
                value={state.autxml_cpf}
                onChange={(e) => setField('autxml_cpf', e.target.value)}
                maxLength={14}
              />
              <FormField
                label="AutXML CNPJ"
                value={state.autxml_cnpj}
                onChange={(e) => setField('autxml_cnpj', e.target.value)}
                maxLength={18}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <FormField
                label="Total frete"
                type="number"
                step="0.01"
                value={state.total_frete}
                onChange={(e) => setField('total_frete', e.target.value)}
              />
              <FormField
                label="Total seguro"
                type="number"
                step="0.01"
                value={state.total_seguro}
                onChange={(e) => setField('total_seguro', e.target.value)}
              />
              <FormField
                label="Total desconto"
                type="number"
                step="0.01"
                value={state.total_desc}
                onChange={(e) => setField('total_desc', e.target.value)}
              />
              <FormField
                label="Outras despesas"
                type="number"
                step="0.01"
                value={state.total_outro}
                onChange={(e) => setField('total_outro', e.target.value)}
              />
              <div>
                <label className="field-label">Modalidade frete</label>
                <select
                  className="field-input"
                  value={state.mod_frete}
                  onChange={(e) => setField('mod_frete', e.target.value)}
                >
                  <option value="9">9 - Sem frete</option>
                  <option value="0">0 - Por conta do emitente</option>
                  <option value="1">1 - Por conta do destinatario</option>
                  <option value="2">2 - Por conta de terceiros</option>
                  <option value="3">3 - Proprio remetente</option>
                  <option value="4">4 - Proprio destinatario</option>
                </select>
              </div>
            </div>

            <div className="space-y-3">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={state.difal_ativar}
                  onChange={(e) => setField('difal_ativar', e.target.checked)}
                />
                Aplicar DIFAL / ICMSUFDest
              </label>
              {state.difal_ativar && (
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <FormField label="DIFAL BC" type="number" step="0.01" value={state.difal_vbc} onChange={(e) => setField('difal_vbc', e.target.value)} />
                  <FormField label="ICMS UF Dest %" type="number" step="0.01" value={state.difal_picmsufdest} onChange={(e) => setField('difal_picmsufdest', e.target.value)} />
                  <FormField label="ICMS Inter %" type="number" step="0.01" value={state.difal_picmsinter} onChange={(e) => setField('difal_picmsinter', e.target.value)} />
                  <FormField label="Partilha %" type="number" step="0.01" value={state.difal_picmsinterpart} onChange={(e) => setField('difal_picmsinterpart', e.target.value)} />
                  <FormField label="ICMS Dest" type="number" step="0.01" value={state.difal_vicmsufdest} onChange={(e) => setField('difal_vicmsufdest', e.target.value)} />
                  <FormField label="ICMS Remet" type="number" step="0.01" value={state.difal_vicmsufremet} onChange={(e) => setField('difal_vicmsufremet', e.target.value)} />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <FormField
                label="Transportador CPF/CNPJ"
                value={state.transp_doc}
                onChange={(e) => setField('transp_doc', e.target.value)}
                className="md:col-span-2"
              />
              <FormField
                label="Nome transportador"
                value={state.transp_nome}
                onChange={(e) => setField('transp_nome', e.target.value)}
                className="md:col-span-2"
              />
              <FormField
                label="IE transportador"
                value={state.transp_ie}
                onChange={(e) => setField('transp_ie', e.target.value)}
              />
              <FormField
                label="UF"
                value={state.transp_uf}
                onChange={(e) => setField('transp_uf', e.target.value)}
                maxLength={2}
              />
              <FormField
                label="Endereço transportador"
                value={state.transp_endereco}
                onChange={(e) => setField('transp_endereco', e.target.value)}
                className="md:col-span-3"
              />
              <FormField
                label="Município transportador"
                value={state.transp_municipio}
                onChange={(e) => setField('transp_municipio', e.target.value)}
                className="md:col-span-3"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <FormField label="Qtd volumes" type="number" step="1" value={state.vol_q} onChange={(e) => setField('vol_q', e.target.value)} />
              <FormField label="Espécie" value={state.vol_esp} onChange={(e) => setField('vol_esp', e.target.value)} />
              <FormField label="Marca" value={state.vol_marca} onChange={(e) => setField('vol_marca', e.target.value)} />
              <FormField label="Numeração" value={state.vol_numeracao} onChange={(e) => setField('vol_numeracao', e.target.value)} />
              <FormField label="Peso líquido" type="number" step="0.001" value={state.vol_peso_liq} onChange={(e) => setField('vol_peso_liq', e.target.value)} />
              <FormField label="Peso bruto" type="number" step="0.001" value={state.vol_peso_bruto} onChange={(e) => setField('vol_peso_bruto', e.target.value)} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
              <FormField label="Fatura" value={state.fat_numero} onChange={(e) => setField('fat_numero', e.target.value)} />
              <FormField label="Valor original" type="number" step="0.01" value={state.fat_valor_original} onChange={(e) => setField('fat_valor_original', e.target.value)} />
              <FormField label="Desc. fatura" type="number" step="0.01" value={state.fat_valor_desconto} onChange={(e) => setField('fat_valor_desconto', e.target.value)} />
              <FormField label="Valor líquido" type="number" step="0.01" value={state.fat_valor_liquido} onChange={(e) => setField('fat_valor_liquido', e.target.value)} />
              <FormField label="Duplicata" value={state.dup_numero} onChange={(e) => setField('dup_numero', e.target.value)} />
              <FormField label="Vencimento" type="date" value={state.dup_vencimento} onChange={(e) => setField('dup_vencimento', e.target.value)} />
              <FormField label="Valor dup." type="number" step="0.01" value={state.dup_valor} onChange={(e) => setField('dup_valor', e.target.value)} />
            </div>

            <div>
              <label className="field-label">Informacoes adicionais</label>
              <textarea
                className="field-input"
                rows={3}
                value={state.informacoes_adicionais}
                onChange={(e) => setField('informacoes_adicionais', e.target.value)}
              />
            </div>
          </section>
        )}

        {isCTe && (
          <>
            {duplicando && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-sm dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200">
                <strong>Base de duplicação carregada via XML AUTORIZADO.</strong> Cole a nova chave da NF-e e ajuste valores/peso se necessário.
              </div>
            )}
            {cteState.chave_nfe_original && (
              <div className="card-surface p-3 text-xs flex items-center gap-2">
                <span className="eyebrow">Chave NF-e da nota original</span>
                <code className="mono text-neutral-700 truncate flex-1 dark:text-neutral-300">{cteState.chave_nfe_original}</code>
              </div>
            )}

            {/* Identificação */}
            <section className="card-surface p-4 space-y-4">
              <h2 className="eyebrow">Identificação</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="field-label">Tipo de serviço</label>
                  <select className="field-input" value={cteState.tp_serv} onChange={(e) => setCteField('tp_serv', e.target.value)}>
                    <option value="0">Normal</option>
                    <option value="1">Subcontratação</option>
                    <option value="2">Redespacho</option>
                    <option value="3">Redespacho intermediário</option>
                    <option value="4">Serviço vinculado multimodal</option>
                  </select>
                </div>
                <div>
                  <label className="field-label">Tipo CT-e</label>
                  <select className="field-input" value={cteState.tp_cte} onChange={(e) => setCteField('tp_cte', e.target.value)}>
                    <option value="0">Normal</option>
                    <option value="1">Complementar</option>
                    <option value="2">Anulação</option>
                    <option value="3">Substituto</option>
                  </select>
                </div>
                <FormField label="CFOP" value={cteState.cfop} onChange={(e) => setCteField('cfop', e.target.value)} maxLength={4} />
                <FormField label="Série" type="number" value={cteState.serie} onChange={(e) => setCteField('serie', e.target.value)} />
                <FormField
                  label="Natureza da operação"
                  value={cteState.natureza_operacao}
                  onChange={(e) => setCteField('natureza_operacao', e.target.value)}
                  className="md:col-span-3"
                />
                <FormField label="Número proposto (opc.)" type="number" value={cteState.numero_proposto} onChange={(e) => setCteField('numero_proposto', e.target.value)} />
              </div>
            </section>

            {/* Rota e valores */}
            <section className="card-surface p-4 space-y-4">
              <h2 className="eyebrow">Rota e valores</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="eyebrow text-emerald-400">Origem</div>
                  <div className="grid grid-cols-3 gap-2">
                    <FormField label="Município" className="col-span-2" value={cteState.xmun_ini} onChange={(e) => setCteField('xmun_ini', e.target.value)} />
                    <FormField label="UF" value={cteState.uf_origem} onChange={(e) => setCteField('uf_origem', e.target.value)} maxLength={2} />
                  </div>
                  <FormField label="Código IBGE origem" value={cteState.cmun_ini} onChange={(e) => setCteField('cmun_ini', e.target.value)} />
                </div>
                <div className="space-y-3">
                  <div className="eyebrow text-indigo-400">Destino</div>
                  <div className="grid grid-cols-3 gap-2">
                    <FormField label="Município" className="col-span-2" value={cteState.xmun_fim} onChange={(e) => setCteField('xmun_fim', e.target.value)} />
                    <FormField label="UF" value={cteState.uf_destino} onChange={(e) => setCteField('uf_destino', e.target.value)} maxLength={2} />
                  </div>
                  <FormField label="Código IBGE destino" value={cteState.cmun_fim} onChange={(e) => setCteField('cmun_fim', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FormField
                  label="Valor do frete (R$)"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={cteState.valor_prestacao}
                  onFocus={(e) => e.currentTarget.select()}
                  onChange={(e) => setValorPrestacaoCTe(e.target.value)}
                  onBlur={(e) => formatarValorPrestacaoCTe(e.target.value)}
                />
                <FormField
                  label="Valor a receber (R$)"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={cteState.valor_receber}
                  onFocus={(e) => e.currentTarget.select()}
                  onChange={(e) => setCteField('valor_receber', e.target.value)}
                  onBlur={(e) => setCteField('valor_receber', formatDecimalBR(parseMoedaBR(e.target.value)))}
                />
                <FormField
                  label="Valor da carga (R$)"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={cteState.valor_total_nota}
                  onFocus={(e) => e.currentTarget.select()}
                  onChange={(e) => setCteField('valor_total_nota', e.target.value)}
                  onBlur={(e) => setCteField('valor_total_nota', formatDecimalBR(parseMoedaBR(e.target.value)))}
                />
              </div>
            </section>

            {/* NFe vinculada */}
            <section className="card-surface p-4 space-y-4">
              <h2 className="eyebrow">Documento vinculado</h2>
              <FormField
                label="Chave NF-e transportada (44 dígitos)"
                value={cteState.chave_nfe_vinculada}
                onChange={(e) => setCteField('chave_nfe_vinculada', e.target.value)}
                placeholder="Cole aqui a chave da NF-e (44 dígitos)"
                ref={chaveNfeRef}
                maxLength={44}
                autoFocus={duplicando}
                hint="A chave da NF-e original está acima, apenas para referência."
              />
              {cteState.tp_serv === '1' && (
                <FormField
                  label="Chave do CT-e original (subcontratação — 44 dígitos)"
                  value={cteState.chave_cte_pai}
                  onChange={(e) => setCteField('chave_cte_pai', e.target.value)}
                  placeholder="Cole a chave do CT-e que está sendo subcontratado"
                  maxLength={44}
                  hint="Obrigatório para subcontratação. CFOP é forçado para 5932 (intra) ou 6932 (inter) automaticamente."
                />
              )}
            </section>

            <section className="card-surface p-4 space-y-4">
              <h2 className="font-semibold text-sm uppercase tracking-wide text-neutral-600 dark:text-neutral-400">Partes</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PessoaAutocomplete
                  label="Tomador"
                  value={cteState.tomador_id}
                  onChange={(id) => setCteField('tomador_id', id ? String(id) : '')}
                  required
                />
                <PessoaAutocomplete
                  label="Remetente"
                  value={cteState.remetente_id}
                  onChange={(id) => setCteField('remetente_id', id ? String(id) : '')}
                  required
                />
                <PessoaAutocomplete
                  label="Destinatário"
                  value={cteState.destinatario_id}
                  onChange={(id) => setCteField('destinatario_id', id ? String(id) : '')}
                  required
                />
                <PessoaAutocomplete
                  label="Expedidor"
                  value={cteState.expedidor_id}
                  onChange={(id) => setCteField('expedidor_id', id ? String(id) : '')}
                  optional
                />
                <PessoaAutocomplete
                  label="Recebedor"
                  value={cteState.recebedor_id}
                  onChange={(id) => setCteField('recebedor_id', id ? String(id) : '')}
                  optional
                  className="md:col-span-2"
                />
              </div>
            </section>

            {/* Dados da carga */}
            <section className="card-surface p-4 space-y-4">
              <h2 className="eyebrow">Dados da carga</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FormField label="Peso (kg)" type="number" step="0.0001" value={cteState.peso_carga} onChange={(e) => setCteField('peso_carga', e.target.value)} />
                <div>
                  <label className="field-label">Tipo de medida</label>
                  <select className="field-input" value={cteState.tipo_medida} onChange={(e) => setCteField('tipo_medida', e.target.value)}>
                    <option value="PESO BRUTO">Peso bruto</option>
                    <option value="PESO CUBADO">Peso cubado</option>
                    <option value="PESO DECLARADO">Peso declarado</option>
                    <option value="UNIDADE">Unidade</option>
                    <option value="LITRAGEM">Litragem</option>
                  </select>
                </div>
                <FormField label="Produto predominante" value={cteState.produto_predominante} onChange={(e) => setCteField('produto_predominante', e.target.value)} />
              </div>
            </section>

            {/* Modal rodoviário */}
            <section className="card-surface p-4 space-y-4">
              <h2 className="eyebrow">Modal rodoviário</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <FormField label="RNTRC" value={cteState.rntrc} onChange={(e) => setCteField('rntrc', e.target.value)} maxLength={8} />
                <FormField label="CIOT (opcional)" value={cteState.ciot} onChange={(e) => setCteField('ciot', e.target.value)} />
                <FormField label="Placa veículo" value={cteState.placa} onChange={(e) => setCteField('placa', e.target.value)} maxLength={7} />
                <FormField label="RENAVAM" value={cteState.renavam} onChange={(e) => setCteField('renavam', e.target.value)} />
              </div>
            </section>

            {tipoDoc === 'cte-os' && (
              <section className="card-surface p-4 space-y-4">
                <h2 className="eyebrow">CT-e OS — RodoOS e Percurso</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormField label="Descrição do serviço" value={cteState.desc_servico} onChange={(e) => setCteField('desc_servico', e.target.value)} maxLength={30} />
                  <FormField label="Qtd. pessoas" type="number" step="0.0001" value={cteState.qtd_pessoas} onChange={(e) => setCteField('qtd_pessoas', e.target.value)} />
                  <FormField label="Data da viagem" type="date" value={cteState.data_viagem} onChange={(e) => setCteField('data_viagem', e.target.value)} />
                  <FormField label="Hora da viagem" type="time" value={cteState.hora_viagem} onChange={(e) => setCteField('hora_viagem', e.target.value)} />
                  <label className="space-y-1 text-sm">
                    <span className="font-medium text-neutral-700">Tipo de fretamento</span>
                    <select className="field-input" value={cteState.tipo_fretamento} onChange={(e) => setCteField('tipo_fretamento', e.target.value)}>
                      <option value="1">Eventual</option>
                      <option value="2">Continuo</option>
                    </select>
                  </label>
                  <FormField label="TAF (Termo de Autorizacao de Fretamento)" value={cteState.taf} onChange={(e) => setCteField('taf', e.target.value)} maxLength={12} />
                  <FormField label="Nº Registro Estadual" value={cteState.nro_reg_estadual} onChange={(e) => setCteField('nro_reg_estadual', e.target.value)} maxLength={12} />
                </div>
                <FormField label="Percurso (UFs separadas por vírgula)" value={cteState.percurso_ufs} onChange={(e) => setCteField('percurso_ufs', e.target.value)} placeholder="Ex.: PB,PE,BA" />
                <p className="text-xs text-neutral-500">Para CT-e OS o percurso é obrigatório. Use siglas de UF separadas por vírgula.</p>
              </section>
            )}

            <section className="card-surface p-4 space-y-2">
              <h2 className="eyebrow">Observações</h2>
              <textarea
                className="field-input"
                rows={3}
                maxLength={2000}
                value={cteState.informacoes_adicionais}
                onChange={(e) => setCteField('informacoes_adicionais', e.target.value)}
                placeholder="Informações complementares de interesse do contribuinte (infCpl)"
              />
            </section>
          </>
        )}

        {isMDFe && (
          <>
            <section className="card-surface p-4 space-y-4">
              <h2 className="eyebrow">Identificação</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <FormField label="UF carregamento" value={mdfeState.uf_ini} onChange={(e) => setMdfeField('uf_ini', e.target.value)} maxLength={2} />
                <FormField label="UF descarga (final)" value={mdfeState.uf_fim} onChange={(e) => setMdfeField('uf_fim', e.target.value)} maxLength={2} />
                <FormField label="Série" type="number" value={mdfeState.serie} onChange={(e) => setMdfeField('serie', e.target.value)} />
                <FormField label="Número proposto (opc.)" type="number" value={mdfeState.numero_proposto} onChange={(e) => setMdfeField('numero_proposto', e.target.value)} />
                <FormField label="Data/hora início viagem (opc.)" type="datetime-local" value={mdfeState.dh_ini_viagem} onChange={(e) => setMdfeField('dh_ini_viagem', e.target.value)} className="md:col-span-2" hint="Vazio = usa o momento da transmissão" />
                <FormField label="Cód. IBGE município carga (7 díg.)" value={mdfeState.cmun_carrega} onChange={(e) => setMdfeField('cmun_carrega', e.target.value)} maxLength={7} />
                <FormField label="Município carregamento" value={mdfeState.xmun_carrega} onChange={(e) => setMdfeField('xmun_carrega', e.target.value)} />
                <FormField label="Percurso (UFs intermediárias, separadas por vírgula)" value={mdfeState.percurso_ufs} onChange={(e) => setMdfeField('percurso_ufs', e.target.value)} className="md:col-span-4" placeholder="Ex: PE, RN" />
              </div>
            </section>

            <section className="card-surface p-4 space-y-4">
              <h2 className="eyebrow">Veículo tracionador</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <FormField label="RNTRC (8 díg.)" value={mdfeState.rntrc} onChange={(e) => setMdfeField('rntrc', e.target.value)} maxLength={8} />
                <FormField label="Placa (7 car.)" value={mdfeState.placa} onChange={(e) => setMdfeField('placa', e.target.value.toUpperCase())} maxLength={7} />
                <FormField label="RENAVAM (opc.)" value={mdfeState.renavam} onChange={(e) => setMdfeField('renavam', e.target.value)} />
                <FormField label="UF do veículo" value={mdfeState.uf_veic} onChange={(e) => setMdfeField('uf_veic', e.target.value)} maxLength={2} />
                <FormField label="Tara (kg)" type="number" value={mdfeState.tara} onChange={(e) => setMdfeField('tara', e.target.value)} />
                <div>
                  <label className="field-label">Tipo rodado</label>
                  <select className="field-input" value={mdfeState.tp_rod} onChange={(e) => setMdfeField('tp_rod', e.target.value)}>
                    <option value="01">01 - Truck</option>
                    <option value="02">02 - Toco</option>
                    <option value="03">03 - Cavalo Mecânico</option>
                    <option value="04">04 - VAN</option>
                    <option value="05">05 - Utilitário</option>
                    <option value="06">06 - Outros</option>
                  </select>
                </div>
                <div>
                  <label className="field-label">Tipo carroceria</label>
                  <select className="field-input" value={mdfeState.tp_car} onChange={(e) => setMdfeField('tp_car', e.target.value)}>
                    <option value="00">00 - Não aplicável</option>
                    <option value="01">01 - Aberta</option>
                    <option value="02">02 - Fechada/Baú</option>
                    <option value="03">03 - Graneleira</option>
                    <option value="04">04 - Porta Container</option>
                    <option value="05">05 - Sider</option>
                  </select>
                </div>
                <FormField label="CIOT (opc.)" value={mdfeState.ciot} onChange={(e) => setMdfeField('ciot', e.target.value)} />
              </div>
            </section>

            <section className="card-surface p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="eyebrow">Condutores</h2>
                <button type="button" className="btn-secondary !py-1" onClick={addCondutor}><Plus className="h-4 w-4" /> Adicionar</button>
              </div>
              <div className="space-y-2">
                {mdfeState.condutores.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end border border-neutral-200 dark:border-neutral-700 rounded-md p-2">
                    <FormField label="Nome" className="col-span-7" value={c.nome} onChange={(e) => updateCondutor(idx, { nome: e.target.value })} />
                    <FormField label="CPF" className="col-span-4" value={c.cpf} onChange={(e) => updateCondutor(idx, { cpf: e.target.value })} maxLength={14} />
                    <button type="button" className="btn-ghost col-span-1 h-10" onClick={() => removeCondutor(idx)} title="Remover"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-neutral-500">Pelo menos um condutor com nome e CPF (11 dígitos) é obrigatório.</p>
            </section>

            <section className="card-surface p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="eyebrow">Descarga e documentos vinculados</h2>
                <button type="button" className="btn-secondary !py-1" onClick={addDescarga}><Plus className="h-4 w-4" /> Adicionar município</button>
              </div>
              <div className="space-y-4">
                {mdfeState.descargas.map((d, idx) => (
                  <div key={idx} className="border border-neutral-200 dark:border-neutral-700 rounded-md p-3 space-y-2">
                    <div className="grid grid-cols-12 gap-2">
                      <FormField label="Cód. IBGE (7 díg.)" className="col-span-3" value={d.cmun} onChange={(e) => updateDescarga(idx, { cmun: e.target.value })} maxLength={7} />
                      <FormField label="Município descarga" className="col-span-8" value={d.xmun} onChange={(e) => updateDescarga(idx, { xmun: e.target.value })} />
                      <button type="button" className="btn-ghost col-span-1 h-10 self-end" onClick={() => removeDescarga(idx)} title="Remover município"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <label className="field-label">Chaves NF-e (44 díg. cada, uma por linha)</label>
                        <textarea className="field-input font-mono text-xs" rows={3} value={d.chaves_nfe} onChange={(e) => updateDescarga(idx, { chaves_nfe: e.target.value })} placeholder="Cole uma ou mais chaves de NF-e (44 dígitos)" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <label className="field-label">Chaves CT-e (44 díg. cada, uma por linha)</label>
                          <button
                            type="button"
                            className="btn-secondary text-[10px] !py-0.5 !px-1.5"
                            title="Busca CT-es autorizadas do emitente atual emitidas hoje e adiciona suas chaves aqui"
                            onClick={async () => {
                              if (!emitenteId) return;
                              try {
                                const r = await fetch(`/api/cte?emitente_id=${emitenteId}&status=autorizada&limit=200`);
                                if (!r.ok) return;
                                const lista = (await r.json()) as Array<{ chave: string | null; data_emissao: string | null }>;
                                const hoje = new Date().toISOString().slice(0, 10);
                                const chavesHoje = lista
                                  .filter((c) => c.chave && c.data_emissao && c.data_emissao.slice(0, 10) === hoje)
                                  .map((c) => c.chave!)
                                  .join('\n');
                                if (!chavesHoje) {
                                  alert('Nenhum CT-e autorizado encontrado emitido hoje para esta empresa.');
                                  return;
                                }
                                const atual = d.chaves_cte.trim();
                                const novo = atual ? `${atual}\n${chavesHoje}` : chavesHoje;
                                updateDescarga(idx, { chaves_cte: novo });
                              } catch (e) { alert('Erro ao buscar CT-es: ' + String(e)); }
                            }}
                          >
                            ↻ CT-es de hoje
                          </button>
                        </div>
                        <textarea className="field-input font-mono text-xs" rows={3} value={d.chaves_cte} onChange={(e) => updateDescarga(idx, { chaves_cte: e.target.value })} placeholder="Cole uma ou mais chaves de CT-e (44 dígitos)" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="card-surface p-4 space-y-4">
              <h2 className="eyebrow">Totais da carga</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FormField label="Valor total da carga (R$)" type="number" step="0.01" value={mdfeState.v_carga} onChange={(e) => setMdfeField('v_carga', e.target.value)} />
                <FormField label="Peso total" type="number" step="0.0001" value={mdfeState.q_carga} onChange={(e) => setMdfeField('q_carga', e.target.value)} />
                <div>
                  <label className="field-label">Unidade do peso</label>
                  <select className="field-input" value={mdfeState.c_unid} onChange={(e) => setMdfeField('c_unid', e.target.value)}>
                    <option value="01">01 - KG</option>
                    <option value="02">02 - Tonelada</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="field-label">Informações adicionais (opc.)</label>
                <textarea className="field-input" rows={3} value={mdfeState.info_adicional} onChange={(e) => setMdfeField('info_adicional', e.target.value)} />
              </div>
            </section>
          </>
        )}

        {!isNFe && !isCTe && !isMDFe && (
          <section className="card-surface p-4 space-y-4">
            <p className="text-sm text-neutral-500">
              Editor de rascunho detalhado disponivel apenas para NF-e, CT-e e MDF-e nesta fase.
              Para {NOMES[tipoDoc]} use o editor legado.
            </p>
          </section>
        )}

        {err && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
            {err}
          </div>
        )}
        {info && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
            {info}
          </div>
        )}
        {result && (
          <pre className="overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 dark:border-slate-800 dark:bg-slate-950/80 dark:text-slate-300">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}

        <div className="flex flex-wrap justify-between gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none">
          <div className="flex gap-2">
            <Link to="/documentos" className="btn-secondary">Voltar</Link>
            {!isNew && (
              <button type="button" onClick={onDelete} className="btn-danger">
                Excluir
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <button type="submit" disabled={saving || transmitting} className="btn-secondary">
              <Save className="h-4 w-4" />
              {saving ? 'Salvando...' : 'Salvar rascunho'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!emitenteId) { setErr('Selecione um emitente antes de emitir.'); return; }
                onSave(null, true);
              }}
              disabled={saving || transmitting}
              className="btn-primary"
              title="Salva o rascunho e transmite para a SEFAZ (ambiente do emitente)"
            >
              <Send className="h-4 w-4" />
              {saving
                ? 'Salvando...'
                : transmitting
                  ? 'Enviando...'
                  : `Emitir ${NOMES[tipoDoc]}`}
            </button>
          </div>
        </div>
      </form>

      <CfopBuscaModal
        open={cfopModalOpen}
        onClose={() => setCfopModalOpen(false)}
        onSelect={(cf) => {
          setField('natureza_operacao', cf.descricao);
          // pré-preenche o CFOP de todos os itens que ainda estão vazios
          setState((s) => ({
            ...s,
            itens: s.itens.map((it) => (it.cfop && it.cfop.trim() ? it : { ...it, cfop: cf.codigo })),
          }));
          setCfopModalOpen(false);
        }}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
